import { Button, Table } from 'antd';
import { Receipt, ShieldAlert } from 'lucide-react';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { TableSkeleton } from '../../../../../shared/components/TableSkeleton';
import { formatMoney } from '../../../../../shared/policies/format-money';
import type { ExpenseView } from '../../../../expenses/expenses.api';
import { ExpenseStateTag } from '../../../../expenses/public/ExpenseStateTag';
import { useTripExpenses } from '../hooks/use-trip-expenses';

/** The expenses captured against this trip, read-only; approving them stays on the Expenses screen. */
export function TripExpensesPanel({ tripId }: { tripId: string }) {
  const { state, retry } = useTripExpenses(tripId);

  if (state.status === 'loading') return <TableSkeleton label="Loading expenses" rows={4} />;
  if (state.status === 'error') {
    return (
      <EmptyState icon={ShieldAlert} title="Couldn't load the expenses" description="Something went wrong reading this trip's expenses.">
        <Button onClick={retry}>Try again</Button>
      </EmptyState>
    );
  }
  if (state.page.total === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="No expenses on this trip"
        description="Expenses the driver captures, or the office records, for this trip appear here."
      />
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <Table<ExpenseView>
        rowKey="id"
        dataSource={state.page.items}
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: 'Date', key: 'date', render: (_, expense) => new Date(expense.deviceTime).toLocaleString() },
          { title: 'Driver', dataIndex: 'driverName', key: 'driver' },
          { title: 'Category', key: 'category', render: (_, expense) => expense.category.name },
          { title: 'Amount', key: 'amount', align: 'right', render: (_, expense) => formatMoney(expense.amount) },
          { title: 'State', key: 'state', render: (_, expense) => <ExpenseStateTag state={expense.state} /> },
        ]}
      />
      {state.page.total > state.page.items.length && (
        <p className="text-sm text-muted">
          Showing the first {state.page.items.length} of {state.page.total} expenses. Filter by this trip on the Expenses screen for the rest.
        </p>
      )}
    </div>
  );
}
